import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const nachricht = data.get("nachricht") as string;
    if (nachricht && nachricht.length > 500) {
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });

    setStatus("sending");
    try {
      const res = await fetch("https://readdy.ai/api/form/d7b8afs9quqsda8uevg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="kontakt" className="py-24 md:py-32 bg-[#F5F5F3]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-20">
          {/* Left: Form */}
          <div className="lg:w-[52%]">
            <span className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-4 block">Kontakt</span>
            <h2 className="text-4xl md:text-5xl font-bold text-black leading-tight mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Jetzt unverbindlich anfragen
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10">
              Lassen Sie uns kurz sprechen – wir zeigen Ihnen, wie wir Ihre Objekte zuverlässig entlasten können.
            </p>

            <form
              id="kontaktformular"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Ihr vollständiger Name"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Firma</label>
                  <input
                    type="text"
                    name="firma"
                    placeholder="Ihr Unternehmen"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Telefonnummer *</label>
                <input
                  type="tel"
                  name="telefon"
                  required
                  placeholder="+49 ..."
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-black transition-colors"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                  Nachricht (optional)
                </label>
                <textarea
                  name="nachricht"
                  rows={4}
                  maxLength={500}
                  placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-black transition-colors resize-none"
                ></textarea>
                <p className={`text-xs mt-1 text-right ${charCount > 480 ? "text-red-500" : "text-gray-400"}`}>
                  {charCount}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-black text-white font-bold py-4 rounded-xl hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-base disabled:opacity-60"
              >
                {status === "sending" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Jetzt Anfrage senden
                  </>
                )}
              </button>

              {status === "success" && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-5 py-4 text-sm flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen.
                </div>
              )}
              {status === "error" && (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-5 py-4 text-sm flex items-center gap-3">
                  <i className="ri-error-warning-line text-lg"></i>
                  Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.
                </div>
              )}
            </form>
          </div>

          {/* Right: Info */}
          <div className="lg:w-[48%] flex flex-col gap-6">
            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-bold text-black text-lg mb-6">Kontaktinformationen</h3>
              <div className="flex flex-col gap-5">
                {[
                  { icon: "ri-phone-line", label: "Telefon", value: "02102 7398070", href: "tel:+492102739807" },
                  { icon: "ri-mail-line", label: "E-Mail", value: "info@igmfacilityservice.de", href: "mailto:info@igmfacilityservice.de" },
                  { icon: "ri-map-pin-line", label: "Adresse", value: "Industriestr. 37, 40878 Ratingen", href: null },
                  { icon: "ri-time-line", label: "Erreichbarkeit", value: "Mo–Fr 10:00–15:00 Uhr · Sa 12:00–15:00 Uhr · Notdienst 24/7", href: null },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 flex-shrink-0">
                      <i className={`${item.icon} text-black text-base`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-gray-800 text-sm font-medium hover:text-black transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-gray-800 text-sm font-medium">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mini About */}
            <div className="bg-[#111] rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://static.readdy.ai/image/42a59d03aabdb3bbb4adf980fce8c22a/c496e7b7b0d35cec41af6fd81216106e.png"
                  alt="IGM Facility Service Logo"
                  className="h-8 w-auto object-contain"
                />
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Wir sind ein eingespieltes Team mit über 50 Mitarbeitern und festen Partnern und betreuen Immobilien zuverlässig in ganz Nordrhein-Westfalen.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                {["Ratingen", "Düsseldorf", "Mettmann", "Erkrath", "Hilden", "NRW-weit"].map((city) => (
                  <span key={city} className="bg-white/10 text-white/80 text-xs px-3 py-1.5 rounded-full">
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
